// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014

import type { SolfegeSystemName } from 'shared/definitions/musicTheory/solfegeAndScaleDegrees';
import type { ModeName } from 'shared/definitions/musicTheory/scales';
import type {
  InteractiveOptions,
  LayoutItem,
  LayoutSettings,
  UserData,
} from './interactivePrototype';
import InteractivePrototype from './interactivePrototype';
import utils from '../utils';
import CheckMark from './checkMark';
import type { List } from './listSorter';
import ListSorter from './listSorter';
import Scale from '../uTheory.Notation/scale';
import { getSolfegeSystemsMapForScaleMode } from './getSolfegeSystemsMapForScaleMode';

interface SolfegeSortLayout extends LayoutSettings {
  sortDiv?: LayoutItem;
}

type TrainableSkills =
  | 'major_scale_solfege'
  | 'major_scale_hand_signs'
  | 'natural_minor_scale_solfege'
  | 'natural_minor_scale_hand_signs'
  | 'harmonic_minor_scale_solfege'
  | 'harmonic_minor_scale_hand_signs'
  | 'melodic_minor_scale_solfege'
  | 'melodic_minor_scale_hand_signs';

export interface SolfegeSortOptions extends InteractiveOptions {
  /* Columns to display. Required. */
  columns: {
    scaleDegrees?: boolean;
    solfege?: boolean;
    handSigns?: boolean;
  };
  /** Defaults to user-defined system */
  solfegeSystem?: SolfegeSystemName;
  /** Default: 'Major' */
  mode?: ModeName;
}

type SolfegeSortColumnNames = keyof SolfegeSortOptions['columns'];

const settings = {
  instructions: true,
  letters: {
    checkButton: true,
    letterButtons: false,
  },
  progressTracker: true,
  checkMark: {},
};

const layout = {
  letters: { percent: 10 },
};

/**
 * Sort and match solfege, scale degrees and hand signs.
 */
class SolfegeSort extends InteractivePrototype {
  declare layout: SolfegeSortLayout;

  declare options: SolfegeSortOptions;

  private solfegeSystem: SolfegeSystemName;

  listDiv: HTMLDivElement;

  attempts: number = 0;

  listSorter: ListSorter;

  // Public constructor
  constructor(displayDiv, options: SolfegeSortOptions, userData?: UserData) {
    super(displayDiv, options, userData);
    this.settings = utils.clone(settings);
    this.layout = utils.clone(layout);

    this.init(displayDiv, options, userData);
  }

  init(displayDiv, options: SolfegeSortOptions, userData?: UserData) {
    // validate the options:
    // must have at least two columns visible:
    const columns: (keyof SolfegeSortOptions['columns'])[] = <SolfegeSortColumnNames[]>(
      ['scaleDegrees', 'solfege', 'handSigns'].filter((column) => !!this.options.columns[column])
    );
    if (columns.length < 2) {
      return Promise.reject(
        `SolfegeSort must have at least two enabled columns, received only ${columns}`
      );
    }

    if (
      options.mode &&
      ![
        'Major',
        'naturalMinor',
        'harmonicMinor',
        'melodicMinorAscending',
        'melodicMinorDescending',
      ].includes(options.mode)
    ) {
      return Promise.reject(`${options.mode} is not a valid mode for solfegeSort.`);
    }

    if (
      options.solfegeSystem &&
      !['scaleDegrees', 'solfegeDoMinor', 'solfegeLaMinor'].includes(options.solfegeSystem)
    ) {
      return Promise.reject(
        `${options.solfegeSystem} is not a valid solfege system for solfegeSort.`
      );
    }
    const userSolfegeSystem = userData?.userStore?.preferences?.solfegeMethod || 'solfegeDoMinor';
    this.solfegeSystem =
      options.solfegeSystem || userSolfegeSystem === 'scaleDegrees'
        ? 'solfegeDoMinor'
        : userSolfegeSystem;

    // now call the prototype's init function:
    return super.init(displayDiv, options, userData).then(() => {
      console.log('target speed', this.targetSpeed);
      this.progressTracker.start(this.targetSpeed);
      this.letterPanel.addCheckCallback(this.checkAnswer, this);
      this.attempts = 0;
    });
  }

  get skills(): TrainableSkills[] {
    const skills: TrainableSkills[] = [];
    if (this.options.mode === 'Major') {
      if (this.options.columns.solfege) {
        skills.push('major_scale_solfege');
      }
      if (this.options.columns.handSigns) {
        skills.push('major_scale_hand_signs');
      }
    }
    if (this.options.mode === 'naturalMinor') {
      if (this.options.columns.solfege) {
        skills.push('natural_minor_scale_solfege');
      }
      if (this.options.columns.handSigns) {
        skills.push('natural_minor_scale_hand_signs');
      }
    }
    if (this.options.mode === 'harmonicMinor') {
      if (this.options.columns.solfege) {
        skills.push('harmonic_minor_scale_solfege');
      }
      if (this.options.columns.handSigns) {
        skills.push('harmonic_minor_scale_hand_signs');
      }
    }
    if (
      this.options.mode === 'melodicMinorAscending' ||
      this.options.mode === 'melodicMinorDescending'
    ) {
      if (this.options.columns.solfege) {
        skills.push('melodic_minor_scale_solfege');
      }
      if (this.options.columns.handSigns) {
        skills.push('melodic_minor_scale_hand_signs');
      }
    }
    return skills;
  }

  displayInstructions() {
    const columns: (keyof SolfegeSortOptions['columns'])[] = <SolfegeSortColumnNames[]>(
      ['scaleDegrees', 'solfege', 'handSigns'].filter((column) => !!this.options.columns[column])
    );
    const columnNames = columns.map(
      (column) =>
        ({
          handSigns: 'hand signs',
          scaleDegrees: 'scale degrees',
          solfege: 'solfege',
        }[column])
    );

    const dragableColumnNames = utils.listToString(columnNames.slice(1));
    const fixedColumnName = columnNames[0];
    const scale = new Scale('C', this.options.mode || 'Major');
    const modeString = scale.modeToString();
    const direction = modeString.includes('melodic') ? `${scale.directionToString()} ` : '';
    this.instructionsDiv.innerHTML = `Drag & sort the ${dragableColumnNames} to match the ${fixedColumnName} in ${direction}${scale.modeToString()}.`;
  }

  createCustomDivs() {
    this.createDiv('listDiv', { width: '100%', height: 'auto' });
    this.displayDiv.removeChild(this.listDiv);
    this.displayDiv.insertBefore(this.listDiv, this.lettersDiv);
    this.checkMark = new CheckMark(this.listDiv);
  }

  initializeCustomElements() {
    const lists: List[] = [];
    const { scaleDegrees, solfege, handSigns } = getSolfegeSystemsMapForScaleMode(
      this.options.mode,
      this.solfegeSystem
    );

    if (this.options.columns.scaleDegrees) {
      lists.push([
        'Scale Degree:',
        ...scaleDegrees.map((d) => ({ classes: 'accidentals', label: `${d.displayString}` })),
      ]);
    }

    if (this.options.columns.solfege) {
      const readableSolfegeName =
        this.options.mode === 'Major'
          ? ''
          : this.solfegeSystem === 'solfegeDoMinor'
          ? 'Do Minor'
          : 'La Minor';

      const solfegeName = readableSolfegeName
        ? `<p>Solfege<br /><small>${readableSolfegeName}</small></p>`
        : 'Solfege';

      lists.push([{ label: solfegeName }, ...solfege]);
    }

    if (this.options.columns.handSigns) {
      lists.push([
        'Hand Sign:',
        // ...handSigns.map((g) => g.alt),
        ...handSigns.map(({ svgString, alt }) => ({
          label: svgString,
          ariaLabel: alt,
          dataID: alt,
        })),
      ]);
    }
    console.log({ solfege });
    // const lists = [scaleDegrees, scaleDegreeNames];
    this.listSorter = new ListSorter(this.listDiv, lists);
    this.layout.sortDiv = {
      div: this.listDiv,
      visible: true,
      percent: 85,
      callback: this.listSorter.resize.bind(this.listSorter),
    };
  }

  gameOver() {
    this.lettersDiv.style.display = 'none';
    this.listSorter.gameOver();
    super.gameOver();
  }

  exit() {
    this.listSorter?.gameOver();
    super.exit();
  }

  checkAnswer() {
    this.attempts++;
    const answers = this.listSorter.check();
    const skillsMap = this.skills.map((skill) => ({
      skill,
      right: answers.right,
      wrong: answers.wrong,
      questions: answers.right + answers.wrong,
    }));
    const gameOver = answers.wrong === 0;
    this.progressTracker.multipleAnswers(skillsMap, answers.right + answers.wrong, gameOver);
    if (gameOver) {
      this.checkMark.check(2);
      this.gameOver();
    } else {
      this.checkMark.x();
      if (this.attempts > 1) this.listSorter.hint();
    }
  }

  static registry: {
    description: string;
    skills: TrainableSkills[];
    options: SolfegeSortOptions;
  }[] = [
    {
      description: 'Scale Degree/Solfege Sort (Major)',
      skills: ['major_scale_solfege'],
      options: {
        columns: {
          scaleDegrees: true,
          solfege: true,
        },
        mode: 'Major',
      },
    },
    {
      description: 'Scale Degree/Solfege Sort (Natural Minor)',
      skills: ['natural_minor_scale_solfege'],
      options: {
        columns: {
          scaleDegrees: true,
          solfege: true,
        },
        mode: 'naturalMinor',
      },
    },
    {
      description: 'Scale Degree/Solfege Sort (Harmonic Minor)',
      skills: ['harmonic_minor_scale_solfege'],
      options: {
        columns: {
          scaleDegrees: true,
          solfege: true,
        },
        mode: 'harmonicMinor',
      },
    },
    {
      description: 'Scale Degree/Solfege Sort (Melodic Minor)',
      skills: ['melodic_minor_scale_solfege'],
      options: {
        columns: {
          scaleDegrees: true,
          solfege: true,
        },
        mode: 'melodicMinorAscending',
      },
    },
  ];
}

export default SolfegeSort;
